import { Observable } from 'rxjs';
import _ from 'lodash';
import { formValueSelector } from 'redux-form/immutable';

import { showErrorAction, showMessageAction } from 'containers/Notification/actions';
import { setPendingExternalTotalAction, updateMultipleAction } from 'entities/donation/actions';
import DonationsApi from 'entities/donation/api';
import { selectDonationMatchingRules } from 'entities/donationMatchingRule/selectors';
import DonationMatchingRulesApi from 'entities/donationMatchingRule/api';
import { selectUser } from 'entities/user/selectors';
import { Privileges } from 'utils/privileges';

import {
  initSuccessAction,
  initFailedAction,
  fetchSuccessAction,
  fetchFailedAction,
  matchDonationsSuccessAction,
  matchDonationsFailedAction,
  unmatchDonationsSuccessAction,
  unmatchDonationsFailedAction,
  cancelCorporateDonationSuccessAction,
  cancelCorporateDonationFailedAction,
} from './actions';
import {
  INIT,
  INIT_SUCCESS,
  MATCH_DONATIONS,
  UNMATCH_DONATIONS,
  FORM_IDENTIFIER,
  SET_FETCH_PARAMS,
  CANCEL_CORPORATE_DONATION,
} from './constants';
import { selectFetchParams } from './selectors';
import { selectPendingExternalTotal } from 'entities/donation/selectors';

const init = (action$, store) =>
  action$.ofType(INIT).mergeMap(() => {
    const state = store.getState();
    const user = selectUser(state);

    const actions = [];

    const donationMatchingRules = selectDonationMatchingRules(state);
    if (user?.hasPrivilege(Privileges.DONATION_MANAGE_RULES) && donationMatchingRules.size === 0) {
      actions.push(DonationMatchingRulesApi.fetchAll());
    }

    const main$ = Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) =>
        Observable.of(
          initFailedAction(e),
          showErrorAction('An error occurred during loading donations.')
        )
      );

    const currentTotal = selectPendingExternalTotal(state);
    if (currentTotal !== null) {
      return main$;
    }

    const pendingExternalDonationMatchRequestsAction =
      DonationsApi.fetchPendingExternalDonationCount().catch(() =>
        Observable.of(
          showErrorAction(
            'An error occurred during loading pending external donation match request count.'
          )
        )
      );

    return Observable.merge(main$, pendingExternalDonationMatchRequestsAction);
  });

const fetch = (action$, store) =>
  action$.ofType(SET_FETCH_PARAMS).mergeMap(() => {
    const state = store.getState();
    const fetchParams = selectFetchParams(state);
    const sort = fetchParams.get('sort');
    const order = fetchParams.get('order');
    const search = fetchParams.get('search');
    const pageNum = fetchParams.get('pageNum');
    const limit = fetchParams.get('limit');
    const fetchParamsType = fetchParams.get('type');
    const type =
      fetchParamsType === 'company-donation-on-user-behalf'
        ? 'CompanyDonationOnUserBehalf'
        : fetchParamsType;
    const skip = limit * pageNum;

    const selector = formValueSelector(FORM_IDENTIFIER);
    const plainState = state.toJS();
    const from = selector(plainState, 'startingAt');
    const to = selector(plainState, 'endingAt');
    const matched = selector(plainState, 'matched');
    const matching = selector(plainState, 'matching');
    const providers = selector(plainState, 'providers');
    const currencies = selector(plainState, 'currencies');
    const frequency = selector(plainState, 'recurringFrequency');
    const paymentMethod = selector(plainState, 'paymentMethod');

    return DonationsApi.fetch({
      limit,
      skip,
      sort,
      order,
      search,
      from: from?.valueOf(),
      to: to?.valueOf(),
      matched,
      matching,
      providers,
      currencies,
      frequency,
      paymentMethod,
      type,
    })
      .mergeMap((resultingActions) => [...resultingActions, fetchSuccessAction()])
      .takeUntil(action$.ofType(SET_FETCH_PARAMS))
      .catch((e) =>
        Observable.of(
          fetchFailedAction(e),
          showErrorAction('An error occurred during loading donations.')
        )
      );
  });

const matchDonations = (action$) =>
  action$.ofType(MATCH_DONATIONS).mergeMap((action) =>
    DonationsApi.matchDonations(action.donationMatches)
      .mergeMap((resultingActions) => [
        ...resultingActions,
        matchDonationsSuccessAction(
          resultingActions[0].donations.toArray().map((donation) => donation.get('id'))
        ),
        showMessageAction('Donations successfully matched!'),
      ])
      .catch((e) =>
        Observable.of(
          matchDonationsFailedAction(e),
          showErrorAction('An error occurred when trying to match the donations.')
        )
      )
  );

const unmatchDonations = (action$) =>
  action$.ofType(UNMATCH_DONATIONS).mergeMap((action) =>
    DonationsApi.unmatchDonations(action.donationIds)
      .mergeMap((resultingActions) => [
        ...resultingActions,
        unmatchDonationsSuccessAction(
          resultingActions[0].donations.toArray().map((donation) => donation.get('id'))
        ),
        showMessageAction('Donations successfully unmatched!'),
      ])
      .catch((e) =>
        Observable.of(
          unmatchDonationsFailedAction(e),
          showErrorAction('An error occurred when trying to unmatch the donations.')
        )
      )
  );

const cancelCorporateDonation = (action$) =>
  action$
    .ofType(CANCEL_CORPORATE_DONATION)
    .mergeMap((action) =>
      DonationsApi.cancelCorporateDonation(action.corporateDonation.get('id')).mergeMap(() => [
        cancelCorporateDonationSuccessAction(action.corporateDonation.get('id')),
        updateMultipleAction([action.corporateDonation.set('status', 'Cancelled')]),
        showMessageAction('Donation cancelled!'),
      ])
    )
    .catch((e) =>
      Observable.of(
        cancelCorporateDonationFailedAction(e),
        showErrorAction('An error occurred trying to cancel the donation')
      )
    );
export default [init, fetch, matchDonations, unmatchDonations, cancelCorporateDonation];
